import { Button, Card, Group, List, Text } from '@mantine/core';
import { useRouter } from 'next/navigation';
import { useContext, useMemo } from "react";
import { Guide } from '../../lib/interfaces';
import { TSCachedImg } from "../TSImageCache";
import { useGoogleImages, revokeCachedGooglePlaceImages } from '../../lib/google';
import { SheetsContext } from "../Sheets";
import { showAddEditGuide } from '../AddEditGuide/AddEditGuide';
import { useIsWide } from '../../lib/hooks';

export function GuidesList({ userGuides, userId }: { userGuides: Guide[], userId: string | undefined }) {
    const router = useRouter();
    const sheets = useContext(SheetsContext);
    // Guides sorted by case-insensitive and numeric-aware (e.g., "Item 2" before "Item 10")
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const guides = userGuides.sort((a, b) => collator.compare(a.title ?? '', b.title ?? ''));

    const items = guides.map((item, index) =>
        <Card
            aria-label='Guide Card'
            key={index}
            p={0}
            m={0}
            onClick={() => router.push(`/traveler/${userId}/guide/${item.docid}`)}
        >
            <GuideImage guide={item} />
            <Group gap={0} wrap='nowrap' py={5} justify="space-between" grow>
                <Text px={4} truncate="end">
                    {item.title}
                </Text>
            </Group>
        </Card>);

    if (items.length === 0) {
        return <div
            className='guide-list'
            style={{
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
            }}
        >
            <div style={{ paddingBottom: '1rem' }}>You don&apos;t have any guides yet!</div>
            <Button onClick={() => showAddEditGuide(sheets, {})}>Create your first guide</Button>
        </div>;
    } else {
        // `flex: 1` bounds the list to the tab panel (overriding the global `* { flex-shrink: 0 }`)
        // so it scrolls here rather than overflowing under the footer. Same as SavedPlacesList.
        return <div style={{ flex: 1, overflowY: 'auto' }}>
            <List px='xs' className='guide-list'>{items}</List>
        </div>;
    }
}

function useAppropriateImageUrl(guide: Guide) {
    // If there is no custom image url but there is a place URL, look up the place and
    // use the first image for the place.
    const { googlePics } = useGoogleImages(guide.img ? null : guide.placeid);

    return useMemo(
        () => {
            if (!guide.img && guide.placeid) {
                if (googlePics && googlePics.length > 0) {
                    return googlePics[0].url400h ?? googlePics[0].url;
                } else {
                    return undefined;
                }
            } else {
                return guide.img400h ?? guide.img;
            }
        },
        [ guide.img, guide.placeid, guide.img400h, googlePics ]
    );
}

export function GuideImage(props: { guide: Guide }) {
    const url = useAppropriateImageUrl(props.guide);
    const isWide = useIsWide();
    const imageHeight = (isWide ?? false) ? 'clamp(200px, 26.5vw, 400px)' : '200px';

    return <TSCachedImg
        style={{ height: imageHeight }}
        src={url}
        alt={props.guide.title ?? 'Unknown Guide'}
        lazyLoading
        onFetchFailure={() => {
            // TODO trigger a re-render of this object, which will re-fetch from cache now that we've revoked
            props.guide.placeid && revokeCachedGooglePlaceImages(props.guide.placeid);
        }}
    />;
}
